import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CCard, CCardBody, CCardHeader } from "@coreui/react-pro";
import { useTradingStrategySignals } from "../../../api/hooks/useSignals";
import { formatSignalTimeBrief } from "../../../lib/dates";
import { routePaths, signalDetailPath } from "../../../routes/route-paths";
import { SignalActionBadge } from "../../signals/components/SignalActionBadge";
import { useTradingStrategies } from "../../../api/hooks/useTradingStrategies";

const SHOWN = 3;

/**
 * What the customer's strategies just did, above everything else they could buy.
 *
 * Absent for anyone with nothing unlocked, and while loading or on failure: the Signals page is where
 * those are explained, and the dashboard should not open on a spinner or an error box.
 */
export function LatestSignals() {
  const { t } = useTranslation("signals");
  const { t: tStrategies } = useTranslation("tradingStrategies");
  const query = useTradingStrategySignals();
  const { data: catalogue } = useTradingStrategies();

  const strategies = query.data?.strategies ?? [];
  if (strategies.length === 0) {
    return null;
  }

  const followsAny = strategies.some((strategy) => strategy.following);
  const latest = (query.data?.signals ?? []).slice(0, SHOWN);

  return (
    <CCard className="mb-4">
      <CCardHeader>
        <div className="d-flex justify-content-between align-items-center">
          <strong>{t("dashboard.title")}</strong>
          <Link to={routePaths.signals} className="btn btn-outline-primary btn-sm">
            {t("dashboard.viewAll")}
          </Link>
        </div>
      </CCardHeader>
      {!followsAny ? (
        <CCardBody className="text-body-secondary">
          {t("dashboard.notFollowing")}{" "}
          <Link to={routePaths.signalsConfigure}>{t("dashboard.configure")}</Link>
        </CCardBody>
      ) : latest.length === 0 ? (
        <CCardBody className="text-body-secondary">{t("dashboard.none")}</CCardBody>
      ) : (
        <div className="list-group list-group-flush">
          {latest.map((signal) => {
            const direction = catalogue?.find((s) => s.id === signal.tradingStrategyId)?.direction;
            const side = direction ? ` · ${tStrategies(`sides.${direction}`)}` : "";

            return (
              <Link
                key={signal.id}
                to={signalDetailPath(signal.id)}
                className="list-group-item list-group-item-action d-flex align-items-center gap-3"
              >
                <SignalActionBadge action={signal.action} />
                <span className="flex-grow-1 text-truncate" style={{ minWidth: 0 }}>
                  <span className="fw-semibold">{signal.symbol}</span>
                  <span className="text-body-secondary">
                    {" "}
                    · {signal.tradingStrategyName}
                    {side}
                  </span>
                </span>
                <span className="font-monospace small text-nowrap text-body-secondary">
                  {formatSignalTimeBrief(signal.receivedAt)}
                </span>
              </Link>
            );
          })}
        </div>
      )}
    </CCard>
  );
}
